import { AuthTransaction, MGMAuthError } from '@mgmresorts/mgm-auth-js-sdk';
import { Typography, Button, Input } from '@mgmresorts/wcl-lab';
import { navigate } from 'hookrouter';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { OktaUser } from '../../../@types/okta-user';
import { UNKNOWN_ERROR_MESSAGE } from '../../api/errors';
import { Alert } from '../../components/ui/Alert';
import { Container } from '../../components/ui/Container';
import { NavigationLink } from '../../components/ui/Link';
import { Logo } from '../../components/ui/Logo';
import { PageLayout } from '../../components/ui/PageLayout';
import { useAppRedirect } from '../../hooks/useAppRedirect';
import { useContent } from '../../hooks/useContent';
import { useFieldValidation } from '../../hooks/useFieldValidation';
import { useInput } from '../../hooks/useInput';
import { captureEvent, captureException, captureMessage, createLogRocketIdentify } from '../../monitoring/logrocket';
import { EventAttributes } from '../../utils/analytics/types';
import { useEvent } from '../../utils/analytics/useEvent';
import { usePageLoadEvent } from '../../utils/analytics/usePageLoadEvent';
import { useAuthContext } from '../../utils/auth/AuthProvider';
import { authWithRedirect } from '../../utils/auth/authWithRedirect';
import { ClientId, MaxLengths } from '../../utils/constants';
import { getFeaturesByClientId } from '../../utils/getFeaturesByClientId';
import { linkAccountToPartner } from '../../utils/linkAccountToPartner';
import { usePersistentParameters } from '../../utils/persistParameterContext';
import { isBetMGMClient } from '../../utils/redirect';
import { UserAccountContext } from '../../utils/userAccountContext';
import { emailValidator, passwordValidator } from '../../utils/validators';
import { ResetPasswordForm } from '../ResetPassword/components/ResetPasswordForm';
import { StyledContent as ResetPasswordStyledContent } from '../ResetPassword/styled';

import { StyledContent } from './styled';

export function SignIn() {
  usePageLoadEvent('sign in', 'home');
  const { email } = useContext(UserAccountContext);
  const { params } = usePersistentParameters();
  const { client_id, redirect_uri } = params;
  const { isBypassGuidedSignIn, isHideForgotPasswordLink, isAllowOktaSignInWithRedirect } = getFeaturesByClientId(
    client_id as ClientId,
  );
  if (!email && !isBypassGuidedSignIn) {
    navigate('/', true);
  }
  const trackEvent = useEvent();
  const redirectToApp = useAppRedirect();
  const [error, setErrorState] = useState<null | string>(null);
  const [isLoading, setLoadingState] = useState(false);
  const [isLockedOut, setIsLockedOut] = useState(false);
  const { validateField, invalidFields } = useFieldValidation([emailValidator, passwordValidator]);
  const { authClient } = useAuthContext();
  const { getContentByPath } = useContent();
  const [authTransaction, setAuthTransaction] = useState<AuthTransaction | undefined>();
  const [postLoginStep, setPostLoginStep] = useState<'redirect' | 'password_reset' | 'password_reset:error'>(
    'redirect',
  );
  const [analyticsFieldErrors, setAnalyticsFieldErrors] = useState<string[]>([]);
  const legalText: string | undefined = getContentByPath(`signIn.legalText.${client_id}`);

  const {
    bind: bindPassword,
    value: password,
    setValue: setPasswordValue,
  } = useInput('', 'password', validateField, validateField);
  const { bind: bindUsername, value: login } = useInput(email || '', 'email', validateField, validateField);

  useEffect(() => {
    trackEvent('identity – sign in - start', 'start sign in process', 'signinstart');
  }, [trackEvent]);

  const processSignIn = useCallback(
    async (username, password) => {
      try {
        setLoadingState(true);
        setErrorState(null);

        captureEvent('Sign In', {
          email: username,
          partner: String(params.partner),
        });

        const authResponse: MGMAuthError | void = await authClient.signIn({
          username,
          password,
        });

        const getLockedOutMessage = (): string => {
          let lockedOutMessagePath = 'signIn.message.lockedOut';

          if (isBetMGMClient(client_id as string)) {
            lockedOutMessagePath = 'signIn.message.lockedOutBetMgm';
          } else if (isAllowOktaSignInWithRedirect) {
            lockedOutMessagePath = 'signIn.message.lockedOutNoForgotPassword';
          }
          return getContentByPath(lockedOutMessagePath);
        };

        if (authResponse instanceof MGMAuthError && authResponse.transaction?.status === 'PASSWORD_EXPIRED') {
          createLogRocketIdentify('identity user', { username });
          captureMessage('user required to update password upon long', 'sign-in', { username });
          setAuthTransaction(authResponse.transaction);
          setPostLoginStep('password_reset');
          setLoadingState(false);
        } else if (authResponse instanceof MGMAuthError && authResponse.transaction?.status === 'LOCKED_OUT') {
          createLogRocketIdentify('identity user', { username });
          captureMessage('user required to reset password upon long', 'sign-in', { username });
          const lockedOutMessage: string = getLockedOutMessage();
          setErrorState(lockedOutMessage.replaceAll('{REDIRECT_URI}', redirect_uri as string));
          setIsLockedOut(true);
          captureException(authResponse, 'sign-in', { username });
          setLoadingState(false);
        } else if (authResponse instanceof MGMAuthError) {
          trackEvent(
            'identity – sign in - credentials submit error',
            'sign in credentials submit error',
            'signinsubmitfail',
            { errorDisplayed: authResponse.message },
          );
          setErrorState(authResponse?.message || 'Authentication Error');
          captureException(authResponse, 'sign-in', { username });
          setLoadingState(false);
        } else {
          await trackEvent(
            'identity – sign in - credentials submit success',
            'sign in credentials submit success',
            'signinsubmitsuccess',
            { signInState: 'success' },
            true,
          );

          if (params.partner && params.tokenKey) {
            const activeSessionObj = await authClient.hasActiveMemberSession();
            if (activeSessionObj?.activeMemberSession && activeSessionObj?.session?.user) {
              const user = (await activeSessionObj.session.user()) as OktaUser;
              await linkAccountToPartner(params.partner as string, params.tokenKey as string, user.profile.mlifeNumber);
            }
          }

          /**
           * TODO: Implement cleaner solution
           * For BetMGM Okta will handle redirect using signInWithRedirect method in AuthSDK
           */
          if (isAllowOktaSignInWithRedirect) {
            return;
          }

          redirectToApp();
        }
      } catch (err) {
        if (err instanceof Error && err.message === 'Operation Timeout') {
          // last ditch effort to attempt to log the user in.
          authWithRedirect(
            username,
            password,
            authClient,
            async () => {
              await trackEvent(
                'identity – sign in - credentials submit success',
                'sign in credentials submit success',
                'signinsubmitsuccess',
                { signInState: 'success' },
                true,
              );
            },
            () => {
              trackEvent(
                'identity – sign in - credentials submit error',
                'sign in credentials submit error',
                'signinsubmitfail',
                { errorDisplayed: UNKNOWN_ERROR_MESSAGE },
              );
              captureException(err as Error, 'sign-in', { username });
              setErrorState(UNKNOWN_ERROR_MESSAGE);
              setLoadingState(false);
            },
          );
        } else {
          trackEvent(
            'identity – sign in - credentials submit error',
            'sign in credentials submit error',
            'signinsubmitfail',
            { errorDisplayed: UNKNOWN_ERROR_MESSAGE },
          );
          captureException(err as Error, 'sign-in', { username });
          setErrorState(UNKNOWN_ERROR_MESSAGE);
          setLoadingState(false);
        }
      }
    },
    [
      params.partner,
      params.tokenKey,
      authClient,
      client_id,
      isAllowOktaSignInWithRedirect,
      getContentByPath,
      redirect_uri,
      trackEvent,
      redirectToApp,
    ],
  );

  const updatePassword = useCallback(
    async (newPassword: string) => {
      try {
        setLoadingState(true);
        if (authTransaction && authTransaction?.changePassword) {
          const result = await authTransaction.changePassword({ oldPassword: password, newPassword });
          // TODO: add effect for failure here based on transaction state
          if (result.status === 'SUCCESS') {
            captureMessage('password update upon sign in processed sucessfully', 'sign-in-password-update', {
              username: login,
            });
            setPasswordValue(newPassword);
            processSignIn(login, newPassword);
            return;
          }
        }

        throw Error('Invalid transaction state, password reset required upon login');
      } catch (err) {
        captureException(err as Error, 'sign-in-password-update', { username: login });
        setErrorState('Password update failed, please sign in again');
      } finally {
        setLoadingState(false);
      }
    },
    [authTransaction, password, setPasswordValue, processSignIn, login],
  );

  useEffect(() => {
    if (invalidFields.length > 0) {
      const eventAttributes = {} as EventAttributes;
      if (invalidFields.includes('email') && !analyticsFieldErrors.includes('email')) {
        eventAttributes.errorField = 'Email';
        eventAttributes.errorDisplayed = getContentByPath('signIn.fields.email.error');
      }

      if (invalidFields.includes('password') && !analyticsFieldErrors.includes('password')) {
        eventAttributes.errorField = 'Password';
        eventAttributes.errorDisplayed = getContentByPath('signIn.fields.password.error');
      }

      if (eventAttributes.errorField) {
        trackEvent(
          'identity – sign in - credentials input error',
          'sign in credentials input error',
          'signininputerror',
          eventAttributes,
        );
      }
    }

    setAnalyticsFieldErrors(invalidFields);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invalidFields, invalidFields.length]);

  const canRequestSignIn = useMemo(
    () => !invalidFields.length && login && password && !isLoading && !isLockedOut,
    [invalidFields.length, isLoading, login, password, isLockedOut],
  );

  if (['password_reset', 'password_reset:error'].includes(postLoginStep)) {
    return (
      <ResetPasswordStyledContent>
        <PageLayout testId='reset-password'>
          <ResetPasswordForm
            isLoading={isLoading || postLoginStep === 'password_reset:error' ? true : false}
            error={error}
            userInfo={authTransaction?.user}
            resetPassword={updatePassword}
          />
        </PageLayout>
      </ResetPasswordStyledContent>
    );
  }

  return (
    <StyledContent>
      <PageLayout
        testId='route-/signin'
        title={getContentByPath('signIn.title')}
        description={getContentByPath('signIn.description')}
      >
        <Container height='auto'>
          <Logo className='identity__page-logo' />
          <Typography variant='body' weight='regular' className='identity__page-heading' testId='page-title'>
            {getContentByPath('signIn.title')}
          </Typography>
          {error && <Alert text={error} data-testid='sign-in-error' />}
          <form className='identity__form'>
            <Input
              label={getContentByPath('signIn.fields.email.title')}
              id='email'
              testId='email'
              disabled={isLoading || !!email}
              aria-label={getContentByPath('signIn.fields.email.label')}
              className={`identity__field ${invalidFields.includes('email') ? 'with-error' : ''}`}
              error={invalidFields.includes('email')}
              hint={invalidFields.includes('email') ? getContentByPath('signIn.fields.email.error') : ''}
              {...bindUsername}
              maxLength={MaxLengths.Email}
            />
            <Input
              type='password'
              testId='password'
              className={`identity__field password ${invalidFields.includes('password') ? 'with-error' : ''}`}
              label={getContentByPath('signIn.fields.password.title')}
              id='password'
              aria-label={getContentByPath('signIn.fields.password.label')}
              disabled={isLoading}
              error={invalidFields.includes('password')}
              hint={invalidFields.includes('password') ? getContentByPath('signIn.fields.password.error') : ''}
              {...bindPassword}
              maxLength={MaxLengths.Password}
              data-private
            />
            {!isHideForgotPasswordLink && (
              <NavigationLink
                className='identity__link open first'
                onClick={async () => {
                  await trackEvent(
                    'identity – sign in - forgot password',
                    'forgot password - sign in help',
                    'signinforgotplink',
                    {},
                    true,
                  );
                }}
                path='/forgot-password'
              >
                {getContentByPath('signIn.forgotPassword')}
              </NavigationLink>
            )}
            {!!legalText && (
              <Typography variant='body' weight='regular' className='identity__legal-text' testId='legal-text'>
                {legalText}
              </Typography>
            )}
            <Button
              ariaLabel={getContentByPath('signIn.signIn')}
              onClick={e => {
                e?.preventDefault();
                processSignIn(login, password);
              }}
              onKeyPress={e => {
                e.preventDefault();
                if (e.key === 'Enter') {
                  processSignIn(login, password);
                }
              }}
              disabled={!canRequestSignIn}
              sticky
              className='form-buttom identity__form-submit'
              testId='sign-in'
              type='submit'
              loading={isLoading}
              shape='square'
              size='large'
              variant='primary'
            >
              <Typography>{getContentByPath('signIn.signIn')}</Typography>
            </Button>
          </form>
        </Container>
      </PageLayout>
    </StyledContent>
  );
}

SignIn.displayName = 'SignIn';
